import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Send } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

export const ClassMessageDialog = ({
  messageClass, onClose,
  messageSubject, setMessageSubject,
  messageBody, setMessageBody,
  recipientPreview, sending, onSend,
}) => {
  const { t } = useLanguage();

  return (
    <Dialog open={!!messageClass} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="message-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('admin.sendMessageTo')} {messageClass?.name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('admin.messageBodyPlaceholder')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="rounded-sm border border-border bg-muted/40 p-3 text-sm">
            {recipientPreview === null ? (
              <span className="text-muted-foreground">{t('common.loading')}...</span>
            ) : recipientPreview.recipient_count === 0 ? (
              <span className="text-amber-500">{t('admin.messageNoRecipients')}</span>
            ) : (
              <>
                <div className="font-medium">
                  {recipientPreview.recipient_count} {t('admin.messageRecipients')}
                </div>
                {recipientPreview.preview_emails && recipientPreview.preview_emails.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1 truncate">
                    {recipientPreview.preview_emails.join(', ')}
                    {recipientPreview.recipient_count > recipientPreview.preview_emails.length && (
                      <span> +{recipientPreview.recipient_count - recipientPreview.preview_emails.length} {t('common.more') || '...'}</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <Label htmlFor="msg-subject">{t('admin.messageSubject')}</Label>
            <Input
              id="msg-subject" value={messageSubject}
              onChange={(e) => setMessageSubject(e.target.value)}
              placeholder={t('admin.messageSubjectPlaceholder')}
              maxLength={200}
              data-testid="message-subject-input"
            />
          </div>

          <div>
            <Label htmlFor="msg-body">{t('admin.messageBody')}</Label>
            <Textarea
              id="msg-body" value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
              placeholder={t('admin.messageBodyPlaceholder')}
              rows={8} maxLength={5000}
              data-testid="message-body-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {messageBody.length} / 5000
            </p>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose} disabled={sending}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={onSend}
              disabled={sending || !recipientPreview || recipientPreview.recipient_count === 0 || !messageSubject.trim() || !messageBody.trim()}
              className="bg-primary hover:bg-primary/90"
              data-testid="message-send-btn"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.sending')}</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />{t('admin.sendNow')}</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
